import React, { useEffect, useState } from "react";
import { History } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { fetchDiagnosticAttemptAudit } from "../../api/fetchDiagnosticAttemptAudit";
import { isCancellation } from "../../utils/rejections";
import type {
  DiagnosticAuditEntry,
  DiagnosticAuditField,
} from "../../types/diagnostic";

/**
 * Every field the audit trail can record. Falling through to a single default
 * label would report a corrected trade selection or a changed stage as an
 * archive event, which is exactly the sort of quiet misattribution an audit
 * trail exists to prevent.
 */
const FIELD_LABELS: Record<DiagnosticAuditField, string> = {
  response: "Answer",
  coach_notes: "Coach notes",
  final_outcome: "Final outcome",
  final_stage: "Stage",
  archived: "Archive state",
  trades: "Trades assessed",
  superseded: "Superseded by a newer diagnostic",
};

const entryLabel = (entry: DiagnosticAuditEntry): string =>
  entry.field === "response"
    ? (entry.questionText ?? FIELD_LABELS.response)
    : (FIELD_LABELS[entry.field] ?? entry.field);

/**
 * Edit history for a corrected attempt. Only rendered when the attempt has
 * recorded edits, so the common untouched case costs no request.
 */
export const AuditTrail: React.FC<{ attemptId: number }> = ({ attemptId }) => {
  const [entries, setEntries] = useState<DiagnosticAuditEntry[]>([]);
  const [status, setStatus] = useState<"loading" | "loaded" | "failed">(
    "loading",
  );

  useEffect(() => {
    const controller = new AbortController();
    setStatus("loading");
    fetchDiagnosticAttemptAudit(attemptId, controller.signal)
      .then((loaded) => {
        setEntries(loaded);
        setStatus("loaded");
      })
      .catch((error) => {
        // Not local error handling: the global interceptor has already toasted
        // this. All that is recorded here is that the panel has nothing to
        // render, so it stops standing on a skeleton for ever — a render
        // state, not an error state.
        if (isCancellation(error)) return;
        setStatus("failed");
      });
    return () => controller.abort();
  }, [attemptId]);

  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
        <History className="h-3 w-3" />
        Edit history
      </div>
      {status === "loading" && <Skeleton className="h-16 w-full rounded" />}

      {status === "failed" && (
        <p className="rounded border border-gray-200 bg-white p-3 text-sm text-gray-500 dark:border-slate-700 dark:bg-slate-800 dark:text-gray-400">
          The edit history could not be loaded. Collapse and reopen this row to
          try again.
        </p>
      )}

      {/* Loaded and empty is an answer, not a state still coming — the whole
          point of naming the three (see `LoadStatus`). Without a branch of its
          own the heading stood over a blank space, which is the reading that
          class of bug exists to stop. It is worth saying rather than hiding
          the panel: this trail is only mounted for an attempt whose edit count
          is non-zero, so an empty history is a disagreement a coach should be
          able to see. */}
      {status === "loaded" && entries.length === 0 && (
        <p className="rounded border border-gray-200 bg-white p-3 text-sm text-gray-500 dark:border-slate-700 dark:bg-slate-800 dark:text-gray-400">
          No edit history is recorded for this diagnostic.
        </p>
      )}

      {status === "loaded" && entries.length > 0 && (
        <ul className="divide-y divide-gray-200 rounded border border-gray-200 bg-white text-sm dark:divide-slate-700 dark:border-slate-700 dark:bg-slate-800">
          {entries.map((entry) => (
            <li className="p-3" key={entry.id}>
              <div className="text-gray-900 dark:text-white">
                {entryLabel(entry)}
              </div>
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                <span className="line-through">
                  {entry.oldValue ?? "empty"}
                </span>
                {" → "}
                <span className="text-gray-900 dark:text-white">
                  {entry.newValue ?? "empty"}
                </span>
              </div>
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {entry.changedBy?.name ?? "Unknown"} ·{" "}
                {new Date(entry.changedAt).toLocaleString()}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AuditTrail;
